import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Plus, X, Briefcase } from 'lucide-react';
import { toast } from 'sonner';
import { projectId } from '../utils/supabase/info';

interface CreateSOWTemplateDialogProps {
  accessToken: string;
  firmId: string;
  onTemplateCreated: () => void;
  editTemplate?: any; // SOWTemplate to edit (if in edit mode)
  onClose?: () => void; // Called when dialog closes (for edit mode)
}

interface EngagementType {
  id: string;
  name: string;
  description: string;
  category: string;
}

const CATEGORIES = [
  { value: 'migration', label: 'Migration Projects' },
  { value: 'implementation', label: 'New Implementations' },
  { value: 'optimization', label: 'Optimization & Tuning' },
  { value: 'upgrade', label: 'Upgrades & Modernization' },
  { value: 'integration', label: 'System Integration' },
  { value: 'training', label: 'Training & Enablement' },
  { value: 'assessment', label: 'Health Checks & Assessments' },
];

const INDUSTRIES = [
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'financial', label: 'Financial Services' },
  { value: 'retail', label: 'Retail / E-commerce' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'technology', label: 'Technology / SaaS' },
  { value: 'education', label: 'Education' },
  { value: 'government', label: 'Government' },
];

export function CreateSOWTemplateDialog({ accessToken, firmId, onTemplateCreated, editTemplate, onClose }: CreateSOWTemplateDialogProps) {
  const [open, setOpen] = useState(false);
  const [engagementTypes, setEngagementTypes] = useState<EngagementType[]>([]);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'migration',
    industry: 'technology',
    projectType: '',
    budgetRange: '$50K - $100K',
    estimatedHours: 160,
    engagementTypeId: '',
    executiveSummary: '',
    scope: '',
    deliverables: [] as string[],
    assumptions: [] as string[],
    exclusions: [] as string[],
    tags: [] as string[],
    paymentTerms: '50% upfront, 50% on completion',
  });

  const [newDeliverable, setNewDeliverable] = useState('');
  const [newAssumption, setNewAssumption] = useState('');
  const [newExclusion, setNewExclusion] = useState('');
  const [newTag, setNewTag] = useState('');

  // Edit mode: control open state externally
  const isEditMode = !!editTemplate;
  const dialogOpen = isEditMode ? !!editTemplate : open;

  // Populate form when editing
  useEffect(() => {
    if (editTemplate) {
      setFormData({
        name: editTemplate.name || '',
        description: editTemplate.description || '',
        category: editTemplate.category || 'migration',
        industry: editTemplate.industry || 'technology',
        projectType: editTemplate.projectType || '',
        budgetRange: editTemplate.budgetRange || '$50K - $100K',
        estimatedHours: editTemplate.estimatedHours || 160,
        engagementTypeId: editTemplate.engagementTypeId || '',
        executiveSummary: editTemplate.executiveSummary || '',
        scope: editTemplate.scope || '',
        deliverables: editTemplate.deliverables || [],
        assumptions: editTemplate.assumptions || [],
        exclusions: editTemplate.exclusions || [],
        tags: editTemplate.tags || [],
        paymentTerms: editTemplate.paymentTerms || '50% upfront, 50% on completion',
      });
    }
  }, [editTemplate]);

  useEffect(() => {
    if (dialogOpen) {
      loadEngagementTypes();
    }
  }, [dialogOpen]);

  const loadEngagementTypes = async () => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/firms/${firmId}/engagement-types`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setEngagementTypes(data.engagementTypes || []);
      }
    } catch (error) {
      console.error('Error loading engagement types:', error);
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Template name is required');
      return;
    }

    if (!formData.description.trim()) {
      toast.error('Template description is required');
      return;
    }

    if (formData.deliverables.length === 0) {
      toast.error('Add at least one deliverable');
      return;
    }

    try {
      setSaving(true);

      // Get engagement type name
      const selectedEngagement = engagementTypes.find(et => et.id === formData.engagementTypeId);

      const templateData = {
        ...formData,
        engagementTypeName: selectedEngagement?.name || '',
        phases: [],
        risks: [],
        successCriteria: [],
      };

      const url = isEditMode
        ? `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/firms/${firmId}/sow-templates/${editTemplate.id}`
        : `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/firms/${firmId}/sow-templates`;

      const response = await fetch(url, {
        method: isEditMode ? 'PATCH' : 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(templateData),
      });

      if (response.ok) {
        toast.success(isEditMode ? 'Template updated' : 'Template created');
        
        if (isEditMode && onClose) {
          onClose(); // Close edit dialog
        } else {
          setOpen(false);
        }
        
        resetForm();
        onTemplateCreated();
      } else {
        const error = await response.json();
        toast.error(error.error || `Failed to ${isEditMode ? 'update' : 'create'} template`);
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} template:`, error);
      toast.error(`Failed to ${isEditMode ? 'update' : 'create'} template`);
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: 'migration',
      industry: 'technology',
      projectType: '',
      budgetRange: '$50K - $100K',
      estimatedHours: 160,
      engagementTypeId: '',
      executiveSummary: '',
      scope: '',
      deliverables: [],
      assumptions: [],
      exclusions: [],
      tags: [],
      paymentTerms: '50% upfront, 50% on completion',
    });
    setNewDeliverable('');
    setNewAssumption('');
    setNewExclusion('');
    setNewTag('');
  };

  const addDeliverable = () => {
    if (newDeliverable.trim()) {
      setFormData(prev => ({ ...prev, deliverables: [...prev.deliverables, newDeliverable.trim()] }));
      setNewDeliverable('');
    }
  };

  const addAssumption = () => {
    if (newAssumption.trim()) {
      setFormData(prev => ({ ...prev, assumptions: [...prev.assumptions, newAssumption.trim()] }));
      setNewAssumption('');
    }
  };

  const addExclusion = () => {
    if (newExclusion.trim()) {
      setFormData(prev => ({ ...prev, exclusions: [...prev.exclusions, newExclusion.trim()] }));
      setNewExclusion('');
    }
  };

  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({ ...prev, tags: [...prev.tags, newTag.trim()] }));
      setNewTag('');
    }
  };

  const removeItem = (field: 'deliverables' | 'assumptions' | 'exclusions' | 'tags', index: number) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const selectedEngagement = engagementTypes.find(et => et.id === formData.engagementTypeId);

  return (
    <>
      {!isEditMode && (
        <Button onClick={() => setOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Create Template
        </Button>
      )}

      <Dialog open={dialogOpen} onOpenChange={(newOpen) => {
        if (isEditMode && onClose) {
          onClose(); // Call parent's close handler in edit mode
        } else {
          setOpen(newOpen);
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isEditMode ? 'Edit SOW Template' : 'Create SOW Template'}</DialogTitle>
            <DialogDescription>
              {isEditMode ? 'Update your SOW template' : 'Create a reusable template for your SOW documents'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="font-medium">Basic Information</h3>
              
              <div>
                <Label htmlFor="name">Template Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Jira Cloud Migration Template"
                />
              </div>

              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Brief description of this template"
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="category">Category</Label>
                  <select
                    id="category"
                    value={formData.category}
                    onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    {CATEGORIES.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label htmlFor="industry">Industry</Label>
                  <select
                    id="industry"
                    value={formData.industry}
                    onChange={(e) => setFormData(prev => ({ ...prev, industry: e.target.value }))}
                    className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    {INDUSTRIES.map(ind => (
                      <option key={ind.value} value={ind.value}>{ind.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Engagement Type Selector - NEW */}
              <div>
                <Label htmlFor="engagementType" className="flex items-center gap-2">
                  <Briefcase className="h-4 w-4 text-blue-500" />
                  Link to Engagement Type
                </Label>
                <select
                  id="engagementType"
                  value={formData.engagementTypeId}
                  onChange={(e) => setFormData(prev => ({ ...prev, engagementTypeId: e.target.value }))}
                  className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">None (Generic Template)</option>
                  {engagementTypes.map(et => (
                    <option key={et.id} value={et.id}>{et.name}</option>
                  ))}
                </select>
                {selectedEngagement && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {selectedEngagement.description}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="estimatedHours">Estimated Hours</Label>
                  <Input
                    id="estimatedHours"
                    type="number"
                    value={formData.estimatedHours}
                    onChange={(e) => setFormData(prev => ({ ...prev, estimatedHours: parseInt(e.target.value) || 0 }))}
                  />
                </div>

                <div>
                  <Label htmlFor="budgetRange">Budget Range</Label>
                  <Input
                    id="budgetRange"
                    value={formData.budgetRange}
                    onChange={(e) => setFormData(prev => ({ ...prev, budgetRange: e.target.value }))}
                    placeholder="e.g., $50K - $100K"
                  />
                </div>
              </div>
            </div>

            {/* Executive Summary & Scope */}
            <div className="space-y-4">
              <h3 className="font-medium">Content</h3>
              
              <div>
                <Label htmlFor="executiveSummary">Executive Summary</Label>
                <Textarea
                  id="executiveSummary"
                  value={formData.executiveSummary}
                  onChange={(e) => setFormData(prev => ({ ...prev, executiveSummary: e.target.value }))}
                  placeholder="High-level overview of the engagement"
                  rows={3}
                />
              </div>

              <div>
                <Label htmlFor="scope">Scope</Label>
                <Textarea
                  id="scope"
                  value={formData.scope}
                  onChange={(e) => setFormData(prev => ({ ...prev, scope: e.target.value }))}
                  placeholder="Detailed scope of work"
                  rows={4}
                />
              </div>
            </div>

            {/* Deliverables */}
            <div className="space-y-2">
              <Label>Deliverables *</Label>
              <div className="flex gap-2">
                <Input
                  value={newDeliverable}
                  onChange={(e) => setNewDeliverable(e.target.value)}
                  placeholder="Add a deliverable"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addDeliverable())}
                />
                <Button type="button" onClick={addDeliverable} size="sm">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.deliverables.map((deliverable, index) => (
                  <Badge key={index} variant="secondary" className="gap-1">
                    {deliverable}
                    <X
                      className="h-3 w-3 cursor-pointer"
                      onClick={() => removeItem('deliverables', index)}
                    />
                  </Badge>
                ))}
              </div>
            </div>

            {/* Assumptions */}
            <div className="space-y-2">
              <Label>Assumptions</Label>
              <div className="flex gap-2">
                <Input
                  value={newAssumption}
                  onChange={(e) => setNewAssumption(e.target.value)}
                  placeholder="Add an assumption"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addAssumption())}
                />
                <Button type="button" onClick={addAssumption} size="sm">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.assumptions.map((assumption, index) => (
                  <Badge key={index} variant="outline" className="gap-1">
                    {assumption}
                    <X
                      className="h-3 w-3 cursor-pointer"
                      onClick={() => removeItem('assumptions', index)}
                    />
                  </Badge>
                ))}
              </div>
            </div>

            {/* Exclusions */}
            <div className="space-y-2">
              <Label>Exclusions</Label>
              <div className="flex gap-2">
                <Input
                  value={newExclusion}
                  onChange={(e) => setNewExclusion(e.target.value)}
                  placeholder="Add an exclusion"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addExclusion())}
                />
                <Button type="button" onClick={addExclusion} size="sm">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.exclusions.map((exclusion, index) => (
                  <Badge key={index} variant="outline" className="gap-1">
                    {exclusion}
                    <X
                      className="h-3 w-3 cursor-pointer"
                      onClick={() => removeItem('exclusions', index)}
                    />
                  </Badge>
                ))}
              </div>
            </div>

            {/* Tags */}
            <div className="space-y-2">
              <Label>Tags</Label>
              <div className="flex gap-2">
                <Input
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  placeholder="Add a tag"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                />
                <Button type="button" onClick={addTag} size="sm">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.tags.map((tag, index) => (
                  <Badge key={index} variant="secondary" className="gap-1">
                    {tag}
                    <X
                      className="h-3 w-3 cursor-pointer"
                      onClick={() => removeItem('tags', index)}
                    />
                  </Badge>
                ))}
              </div>
            </div>

            {/* Payment Terms */}
            <div>
              <Label htmlFor="paymentTerms">Payment Terms</Label>
              <Input
                id="paymentTerms"
                value={formData.paymentTerms}
                onChange={(e) => setFormData(prev => ({ ...prev, paymentTerms: e.target.value }))}
                placeholder="e.g., 50% upfront, 50% on completion"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              if (isEditMode && onClose) {
                onClose();
              } else {
                setOpen(false);
              }
            }} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (isEditMode ? 'Saving...' : 'Creating...') : (isEditMode ? 'Save Changes' : 'Create Template')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}